import React from 'react';
import { Activity, Gauge, Workflow, AlertTriangle } from 'lucide-react';
import { useOperationalContext } from '../../contexts/OperationalContext';

interface OperationalMetricsBarProps {
  slaGlobal?: number;
  throughput?: number;
  wip?: number;
  bottleneck?: string | null;
  leadTimeLabel?: string;
  className?: string;
  variant?: 'cards' | 'ruler';
}

function metricCard(
  icon: React.ReactNode,
  label: string,
  value: React.ReactNode,
  toneClass: string,
): React.ReactElement {
  return (
    <div className={`flex items-center gap-2 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 py-3 ${toneClass}`}>
      <span>{icon}</span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">{label}</p>
        <p className="truncate text-[16px] font-semibold text-[var(--text-primary)]">{value}</p>
      </div>
    </div>
  );
}

export const OperationalMetricsBar: React.FC<OperationalMetricsBarProps> = ({
  slaGlobal,
  throughput,
  wip,
  bottleneck,
  leadTimeLabel,
  className,
  variant = 'cards',
}) => {
  const context = useOperationalContext();

  const sla = slaGlobal ?? context.globalSla;
  const flowThroughput = throughput ?? context.throughput7d;
  const userWip = wip ?? context.userWip;
  const bottleneckLabel = bottleneck ?? context.bottleneckStage;

  if (variant === 'ruler') {
    const metricItems = [
      { label: 'SLA global', value: `${sla.toFixed(1)}%` },
      { label: 'Throughput', value: flowThroughput },
      { label: 'WIP', value: userWip },
      { label: 'Gargalo', value: bottleneckLabel || 'Nenhum' },
      { label: 'Lead time medio', value: leadTimeLabel ?? '0h' },
    ];

    return (
      <div className={`overflow-x-auto ${className ?? ''}`.trim()}>
        <div className="grid min-w-max gap-3 md:grid-cols-5">
          {metricItems.map((item) => (
            <div
              key={item.label}
              className="rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-[14px] py-[14px]"
            >
              <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">{item.label}</div>
              <div className="mt-1 truncate text-[20px] font-semibold leading-none text-[var(--text-primary)]">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4 ${className ?? ''}`.trim()}>
      {metricCard(
        <Gauge size={14} className="text-[#60A5FA]" />,
        'SLA Global',
        `${sla.toFixed(1)}%`,
        '',
      )}
      {metricCard(
        <Workflow size={14} className="text-[#60A5FA]" />,
        'Throughput (7d)',
        flowThroughput,
        '',
      )}
      {metricCard(
        <Activity size={14} className="text-[#60A5FA]" />,
        'WIP',
        userWip,
        '',
      )}
      {metricCard(
        <AlertTriangle size={14} className="text-[#60A5FA]" />,
        'Gargalo',
        bottleneckLabel || 'Sem gargalo detectado',
        '',
      )}
    </div>
  );
};

export default OperationalMetricsBar;

